
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Globe, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { createAndEnqueueTask } from '@/utils/edgeFunctionUtils';
import { supabase } from '@/integrations/supabase/client';
import { useFetchAppTags } from '@/hooks/useAppTags';
import { useAvailablePeriods } from '@/hooks/useClaudeTasks';

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { toast } = useToast();

  // Fetch tags and periods for TAG_PAGE task
  const { data: appTags } = useFetchAppTags();
  const { data: availablePeriods } = useAvailablePeriods();

  // Form state for creating tasks
  const [taskType, setTaskType] = useState('EXTRACT_METRICS');
  const [slug, setSlug] = useState('');
  const [version, setVersion] = useState('');
  const [quarter, setQuarter] = useState('');
  const [force, setForce] = useState(false);
  const [useAI, setUseAI] = useState(true);
  const [imageUrl, setImageUrl] = useState('');
  const [tag, setTag] = useState('');
  const [period, setPeriod] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isRunningAllCompanies, setIsRunningAllCompanies] = useState(false);
  const [allCompaniesResult, setAllCompaniesResult] = useState<{ success: boolean; message: string; count?: number; filtered_out?: number; total_slugs?: number } | null>(null);
  const [isRunningRemainingCompanies, setIsRunningRemainingCompanies] = useState(false);
  const [remainingCompaniesResult, setRemainingCompaniesResult] = useState<{ success: boolean; message: string; count?: number; filtered_out?: number } | null>(null);
  const [isRunningExtractTags, setIsRunningExtractTags] = useState(false);
  const [isRunningVerifyAll, setIsRunningVerifyAll] = useState(false);
  const [verifyAllResult, setVerifyAllResult] = useState<{ success: boolean; message: string; count?: number; total_slugs?: number; skipped_count?: number } | null>(null);

  const resetForm = () => {
    setSlug('');
    setVersion('');
    setQuarter('');
    setForce(false);
    setUseAI(true);
    setImageUrl('');
    setTag('');
    setPeriod('');
    setAllCompaniesResult(null);
    setRemainingCompaniesResult(null);
    setVerifyAllResult(null);
    setIsRunningExtractTags(false);
    setIsRunningVerifyAll(false);
  };

  const handleCreateTask = async () => {
    if (!slug) {
      toast({
        title: 'Error',
        description: 'Slug is required',
        variant: 'destructive',
      });
      return;
    }

    // Validate version for VERIFY_IMAGE task
    if (taskType === 'VERIFY_IMAGE' && !version) {
      toast({
        title: 'Error',
        description: 'Version is required for Verify Image task. The image URL will be looked up from the scrapes table using the slug and version.',
        variant: 'destructive',
      });
      return;
    }

    // Validate TAG_PAGE task requirements
    if (taskType === 'TAG_PAGE') {
      if (!period) {
        toast({
          title: 'Error',
          description: 'Period is required for Tag Page task',
          variant: 'destructive',
        });
        return;
      }
      if (!tag) {
        toast({
          title: 'Error',
          description: 'Tag is required for Tag Page task',
          variant: 'destructive',
        });
        return;
      }
    }
    
    setSubmitting(true);
    try {
      console.log(`Creating ${taskType} task for slug: ${slug}`);
      
      // Prepare options based on task type
      const options: Record<string, any> = {};
      
      if (taskType === 'EXTRACT_METRICS' || taskType === 'EXTRACT_TAGS' || taskType === 'VERIFY_IMAGE') {
        if (version) options.version = version;
      }
      
      if (taskType === 'EXTRACT_METRICS' || taskType === 'PROCESS_METRIC_EVENTS') {
        if (quarter) options.quarter = quarter;
      }
      
      if (taskType === 'EXTRACT_METRICS' || taskType === 'SCREENSHOT_PAGE' || taskType === 'EXTRACT_TAGS' || taskType === 'VERIFY_IMAGE') {
        options.force = force;
      }
      
      if (taskType === 'SCREENSHOT_PAGE') {
        options.useAI = useAI;
      }

      if (taskType === 'TAG_PAGE') {
        options.period = period;
        options.tag = tag;
        options.force = force;
      }

      // For VERIFY_IMAGE, the task-enqueue function will get the screenshot_url from the scrapes table
      // using the slug and version parameters
      if (taskType === 'VERIFY_IMAGE') {
        console.log(`Creating VERIFY_IMAGE task for slug: ${slug}, version: ${version}`);
        console.log(`The screenshot URL will be retrieved from the scrapes table during task execution`);
      }

      console.log('Task options:', options);
      
      // Create and enqueue the task using the task-enqueue edge function
      const { data, error } = await createAndEnqueueTask(taskType, slug, options);
      
      if (error) {
        throw error;
      }
      
      toast({
        title: 'Success',
        description: `Task created: ${taskType} for ${slug}`,
      });
      
      // Reset form
      resetForm();
      
      // Notify parent of success
      onSuccess();
      
      // Close modal
      onClose();
    } catch (error: any) {
      console.error('Error creating task:', error);
      toast({
        title: 'Error',
        description: `Failed to create task: ${error.message || 'Unknown error'}`,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // Function to run scrapes for all companies
  const handleRunAllCompaniesScrape = async () => {
    setIsRunningAllCompanies(true);
    setAllCompaniesResult(null);
    
    try {
      console.log(`🔄 [TaskManager] Running scrapes for all companies (force: ${force}, useAI: ${useAI})`);
      
      // Prepare the request body for task-enqueue with the "all" flag
      const requestBody = {
        type: 'SCREENSHOT_PAGE',
        slug: 'batch-processing', // Adding a dummy slug to avoid the validation error
        all: true,
        options: {
          force: force,
          useAI: useAI
        }
      };
      
      console.log(`🔄 [TaskManager] Request body for all companies:`, requestBody);
      
      // Call task-enqueue with the "all" flag using Supabase client
      const { data, error } = await supabase.functions.invoke('task-enqueue', {
        body: requestBody
      });
      
      if (error) {
        throw error;
      }
      console.log(`🔄 [TaskManager] All companies scrape response:`, data);
      
      const result = {
        success: data.success,
        message: data.message,
        count: data.count,
        filtered_out: data.filtered_out
      };
      
      setAllCompaniesResult(result);
      
      console.log('✅ [TaskManager] All companies scrape job submitted:', data);
      toast({
        title: "All companies scrape submitted",
        description: `Initiated scrapes for ${data.count} companies`
      });
      
      // Notify parent of success
      onSuccess();
      
      // Close modal after a short delay
      setTimeout(() => onClose(), 2000);
    } catch (error: any) {
      console.error('❌ [TaskManager] Error running all companies scrape:', error);
      
      setAllCompaniesResult({
        success: false,
        message: error.message || "Error submitting all companies scrape job"
      });
      
      toast({
        variant: "destructive",
        title: "All companies scrape failed",
        description: error.message || "Error submitting all companies scrape job"
      });
    } finally {
      setIsRunningAllCompanies(false);
    }
  };
  
  // Function to run scrapes for remaining companies (those without successful scrapes)
  const handleRunRemainingCompaniesScrape = async () => {
    setIsRunningRemainingCompanies(true);
    setRemainingCompaniesResult(null);
    
    try {
      console.log(`🔄 [TaskManager] Running scrapes for remaining companies (force: ${force}, useAI: ${useAI})`);
      
      // Prepare the request body for task-enqueue with the "remaining" flag
      const requestBody = {
        type: 'SCREENSHOT_PAGE',
        slug: 'batch-processing', // Adding a dummy slug to avoid the validation error
        remaining: true,
        options: {
          force: force,
          useAI: useAI
        }
      };
      
      console.log(`🔄 [TaskManager] Request body for remaining companies:`, requestBody);
      
      // Call task-enqueue with the "remaining" flag using Supabase client
      const { data, error } = await supabase.functions.invoke('task-enqueue', {
        body: requestBody
      });
      
      if (error) {
        throw error;
      }
      
      console.log(`🔄 [TaskManager] Remaining companies scrape response:`, data);
      
      const result = {
        success: data.success,
        message: data.message,
        count: data.count,
        filtered_out: data.filtered_out
      };
      
      setRemainingCompaniesResult(result);
      
      console.log('✅ [TaskManager] Remaining companies scrape job submitted:', data);
      toast({
        title: "Remaining companies scrape submitted",
        description: `Initiated scrapes for ${data.count} companies (${data.filtered_out} already completed)`
      });
      
      // Notify parent of success
      onSuccess();
      
      // Close modal after a short delay
      setTimeout(() => onClose(), 2000);
    } catch (error: any) {
      console.error('❌ [TaskManager] Error running remaining companies scrape:', error);
      
      setRemainingCompaniesResult({
        success: false,
        message: error.message || "Error submitting remaining companies scrape job"
      });
      
      toast({
        variant: "destructive",
        title: "Remaining companies scrape failed",
        description: error.message || "Error submitting remaining companies scrape job"
      });
    } finally {
      setIsRunningRemainingCompanies(false);
    }
  };
  
  // Function to run extract tags for all companies
  const handleRunAllExtractTags = async () => {
    setIsRunningExtractTags(true);
    setAllCompaniesResult(null);
    
    try {
      console.log(`🔄 [TaskManager] Running extract tags for all companies (force: ${force})`);
      
      // Prepare the request body for task-enqueue with the "all" flag for EXTRACT_TAGS
      const requestBody = {
        type: 'EXTRACT_TAGS',
        slug: 'batch-processing', // Adding a dummy slug to avoid the validation error
        all: true,
        force: force // Pass force flag directly
      };
      
      console.log(`🔄 [TaskManager] Request body for extract tags all:`, requestBody);
      
      // Call task-enqueue with the "all" flag using Supabase client
      const { data, error } = await supabase.functions.invoke('task-enqueue', {
        body: requestBody
      });
      
      if (error) {
        throw error;
      }
      
      console.log(`🔄 [TaskManager] Extract tags all response:`, data);
      
      const result = {
        success: data.success,
        message: data.message,
        count: data.count,
        total_slugs: data.total_slugs,
        skipped_count: data.skipped_count
      };
      
      setAllCompaniesResult(result);
      
      console.log('✅ [TaskManager] Extract tags all job submitted:', data);
      toast({
        title: "Extract tags submitted",
        description: `Initiated tag extraction for ${data.count} verified scrapes${data.skipped_count ? `, skipped ${data.skipped_count} with existing tags` : ''}`
      });
      
      // Notify parent of success
      onSuccess();
      
      // Close modal after a short delay
      setTimeout(() => onClose(), 2000);
    } catch (error: any) {
      console.error('❌ [TaskManager] Error running extract tags all:', error);
      
      setAllCompaniesResult({
        success: false,
        message: error.message || "Error submitting extract tags all job"
      });
      
      toast({
        variant: "destructive",
        title: "Extract tags all failed",
        description: error.message || "Error submitting extract tags all job"
      });
    } finally {
      setIsRunningExtractTags(false);
    }
  };
  
  // Function to verify all screenshots
  const handleVerifyAllScreenshots = async () => {
    setIsRunningVerifyAll(true);
    setVerifyAllResult(null);
    
    try {
      console.log(`🔄 [TaskManager] Running verification for all screenshots (force: ${force})`);
      
      // Prepare the request body for task-enqueue with the "all" flag for VERIFY_IMAGE
      const requestBody = {
        type: 'VERIFY_IMAGE',
        task_type: 'VERIFY_IMAGE',
        slug: 'batch-processing', // Adding a dummy slug to avoid the validation error
        all: true,
        force: force // Pass force flag directly
      };
      
      console.log(`🔄 [TaskManager] Request body for verify all screenshots:`, requestBody);
      
      // Call task-enqueue with the "all" flag using Supabase client
      const { data, error } = await supabase.functions.invoke('task-enqueue', {
        body: requestBody
      });
      
      if (error) {
        throw error;
      }
      
      console.log(`🔄 [TaskManager] Verify all screenshots response:`, data);
      
      const result = {
        success: data.success,
        message: data.message,
        count: data.count,
        total_slugs: data.total_slugs,
        skipped_count: data.skipped_count
      };
      
      setVerifyAllResult(result);
      
      console.log('✅ [TaskManager] Verify all screenshots job submitted:', data);
      toast({
        title: "Verify screenshots submitted",
        description: `Initiated screenshot verification for ${data.count} scrapes${data.skipped_count ? `, skipped ${data.skipped_count} already verified scrapes` : ''}`
      });
      
      // Notify parent of success
      onSuccess();
      
      // Close modal after a short delay
      setTimeout(() => onClose(), 2000);
    } catch (error: any) {
      console.error('❌ [TaskManager] Error verifying all screenshots:', error);
      
      setVerifyAllResult({
        success: false,
        message: error.message || "Error submitting verify all screenshots job"
      });
      
      toast({
        variant: "destructive",
        title: "Verify all screenshots failed",
        description: error.message || "Error submitting verify all screenshots job"
      });
    } finally {
      setIsRunningVerifyAll(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        onClose();
        resetForm();
      }
    }}>
      <DialogContent className="sm:max-w-md md:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Task</DialogTitle>
          <DialogDescription>
            Configure and create a new data processing task
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="taskType">Task Type</Label>
            <Select value={taskType} onValueChange={setTaskType}>
              <SelectTrigger>
                <SelectValue placeholder="Select task type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EXTRACT_METRICS">Extract Metrics</SelectItem>
                <SelectItem value="EXTRACT_TAGS">Extract Tags</SelectItem>
                <SelectItem value="SCREENSHOT_PAGE">Screenshot Page</SelectItem>
                <SelectItem value="PROCESS_METRIC_EVENTS">Process Metric Events</SelectItem>
                <SelectItem value="VERIFY_IMAGE">Verify Image</SelectItem>
                <SelectItem value="TAG_PAGE">Tag Page</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="slug">Company Slug (required)</Label>
            <Input
              id="slug"
              placeholder="e.g., company-name"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
          </div>
          
          {(taskType === 'EXTRACT_METRICS' || taskType === 'EXTRACT_TAGS') && (
            <div className="space-y-2">
              <Label htmlFor="version">Version (optional)</Label>
              <Input
                id="version"
                placeholder="e.g., 2023-01-01"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
            </div>
          )}
          
          {(taskType === 'EXTRACT_METRICS' || taskType === 'PROCESS_METRIC_EVENTS') && (
            <div className="space-y-2">
              <Label htmlFor="quarter">Quarter (optional)</Label>
              <Input
                id="quarter"
                placeholder="e.g., 2023Q1"
                value={quarter}
                onChange={(e) => setQuarter(e.target.value)}
              />
            </div>
          )}

          {taskType === 'TAG_PAGE' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="period">Period (required)</Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePeriods && availablePeriods.length > 0 ? (
                      availablePeriods.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="loading" disabled>
                        Loading periods...
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="tag">Tag (required)</Label>
                  <a
                    href="/reports/tags"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    Manage Tags
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <Select value={tag} onValueChange={setTag}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select tag" />
                  </SelectTrigger>
                  <SelectContent>
                    {appTags && appTags.length > 0 ? (
                      appTags
                        .filter((t) => t.is_active)
                        .map((t) => (
                          <SelectItem key={t.id} value={t.tag_name}>
                            {t.tag_name}
                          </SelectItem>
                        ))
                    ) : (
                      <SelectItem value="loading" disabled>
                        Loading tags...
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {(taskType === 'EXTRACT_METRICS' || taskType === 'SCREENSHOT_PAGE' || taskType === 'EXTRACT_TAGS' || taskType === 'VERIFY_IMAGE' || taskType === 'TAG_PAGE') && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="force"
                checked={force}
                onCheckedChange={(checked) => setForce(checked as boolean)}
              />
              <Label htmlFor="force">
                {taskType === 'EXTRACT_TAGS'
                  ? 'Force (re-process even if tags already exist)'
                  : taskType === 'VERIFY_IMAGE'
                    ? 'Force (re-verify even if already verified)'
                    : taskType === 'TAG_PAGE'
                      ? 'Force (re-process even if already processed)'
                      : 'Force (re-process even if already exists)'}
              </Label>
            </div>
          )}
          
          {taskType === 'SCREENSHOT_PAGE' && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useAI"
                checked={useAI}
                onCheckedChange={(checked) => setUseAI(checked as boolean)}
              />
              <Label htmlFor="useAI">Use AI for processing</Label>
            </div>
          )}
          
          {taskType === 'VERIFY_IMAGE' && (
            <div className="space-y-2">
              <Label htmlFor="version">Version (required)</Label>
              <Input
                id="version"
                placeholder="e.g., 2023-01-01"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                The screenshot URL will be retrieved from the scrapes table using the slug and version
              </p>
            </div>
          )}
          
          {allCompaniesResult && (
            <div className="p-3 rounded-md border border-gray-200 bg-gray-50 mb-2">
              <div className={`text-sm font-medium ${allCompaniesResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {allCompaniesResult.success ? "All Companies Scrape Initiated" : "All Companies Scrape Failed"}
              </div>
              <div className="text-sm text-gray-600">
                {allCompaniesResult.message}
                {allCompaniesResult.count && (
                  <span className="font-semibold"> ({allCompaniesResult.count} companies)</span>
                )}
                {allCompaniesResult.filtered_out > 0 && (
                  <span className="text-gray-500"> ({allCompaniesResult.filtered_out} already completed)</span>
                )}
                {allCompaniesResult.skipped_count > 0 && (
                  <span className="text-gray-500"> ({allCompaniesResult.skipped_count} skipped - tags already exist)</span>
                )}
              </div>
            </div>
          )}
          
          {remainingCompaniesResult && (
            <div className="p-3 rounded-md border border-gray-200 bg-gray-50 mb-2">
              <div className={`text-sm font-medium ${remainingCompaniesResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {remainingCompaniesResult.success ? "Remaining Companies Scrape Initiated" : "Remaining Companies Scrape Failed"}
              </div>
              <div className="text-sm text-gray-600">
                {remainingCompaniesResult.message}
                {remainingCompaniesResult.count && (
                  <span className="font-semibold"> ({remainingCompaniesResult.count} companies)</span>
                )}
                {remainingCompaniesResult.filtered_out > 0 && (
                  <span className="text-gray-500"> ({remainingCompaniesResult.filtered_out} already completed)</span>
                )}
              </div>
            </div>
          )}
          
          {verifyAllResult && (
            <div className="p-3 rounded-md border border-gray-200 bg-gray-50">
              <div className={`text-sm font-medium ${verifyAllResult.success ? 'text-green-600' : 'text-red-600'}`}>
                {verifyAllResult.success ? "Verify All Screenshots Initiated" : "Verify All Screenshots Failed"}
              </div>
              <div className="text-sm text-gray-600">
                {verifyAllResult.message}
                {verifyAllResult.count && (
                  <span className="font-semibold"> ({verifyAllResult.count} screenshots)</span>
                )}
                {verifyAllResult.skipped_count > 0 && (
                  <span className="text-gray-500"> ({verifyAllResult.skipped_count} skipped - already verified)</span>
                )}
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          {taskType === 'SCREENSHOT_PAGE' && (
            <>
              <Button 
                variant="outline"
                onClick={handleRunAllCompaniesScrape} 
                disabled={isRunningAllCompanies || isRunningRemainingCompanies || submitting || isRunningExtractTags}
                className="flex items-center gap-2 w-full sm:w-auto"
              >
                {isRunningAllCompanies ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running All...
                  </>
                ) : (
                  <>
                    <Globe className="h-4 w-4" />
                    Scrape All Companies
                  </>
                )}
              </Button>
              
              <Button 
                variant="outline"
                onClick={handleRunRemainingCompaniesScrape} 
                disabled={isRunningRemainingCompanies || isRunningAllCompanies || submitting || isRunningExtractTags}
                className="flex items-center gap-2 w-full sm:w-auto"
              >
                {isRunningRemainingCompanies ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running Remaining...
                  </>
                ) : (
                  <>
                    <Globe className="h-4 w-4" />
                    Scrape Remaining
                  </>
                )}
              </Button>
            </>
          )}
          
          {taskType === 'EXTRACT_TAGS' && (
            <Button 
              variant="outline"
              onClick={handleRunAllExtractTags} 
              disabled={isRunningAllCompanies || isRunningRemainingCompanies || submitting || isRunningExtractTags || isRunningVerifyAll}
              className="flex items-center gap-2 w-full sm:w-auto"
            >
              {isRunningExtractTags ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Extracting Tags...
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4" />
                  Extract Tags for Verified Scrapes
                </>
              )}
            </Button>
          )}
          
          {taskType === 'VERIFY_IMAGE' && (
            <Button 
              variant="outline"
              onClick={handleVerifyAllScreenshots} 
              disabled={isRunningAllCompanies || isRunningRemainingCompanies || submitting || isRunningExtractTags || isRunningVerifyAll}
              className="flex items-center gap-2 w-full sm:w-auto"
            >
              {isRunningVerifyAll ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying Screenshots...
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4" />
                  Verify All Screenshots
                </>
              )}
            </Button>
          )}
          
          <Button 
            onClick={handleCreateTask} 
            disabled={submitting || !slug || isRunningAllCompanies || isRunningRemainingCompanies || isRunningExtractTags || isRunningVerifyAll}
            className="w-full sm:w-auto"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Task'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateTaskModal;
